import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { DateRange } from '@blisspointmedia/bpm-types/dist/RelativeDatePresets';
import { MetaLambdaFetch, awaitJSON } from '../../utils/fetch-utils';
import {
  AdKpiVolumeRevenueRow,
  AdPerformanceTotalsRow,
  AdSetDailyPerformanceRow,
  AdSetPerformanceTotalsRow,
  AdsetPlacementBreakdownRow,
  AdsetStatusRow,
  CreativeFatigueSummaryRow,
  CreativeFatiguedAdsRow,
  DiversityLabelScoreRow
} from './meta-fatigue-types';

// ── Company-scoped (no date range) ──────────────────────────────────────────

export const useDiversityLabelScore = (company: string | null) =>
  useQuery({
    queryKey: ['metaFatigueDiversityLabelScore', company],
    queryFn: async () => {
      const res = await MetaLambdaFetch('/getDiversityLabelScore', { params: { company } });
      return awaitJSON<DiversityLabelScoreRow[]>(res);
    },
    enabled: Boolean(company)
  });

export const useCreativeFatigueSummary = (company: string | null) =>
  useQuery({
    queryKey: ['metaFatigueCreativeFatigueSummary', company],
    queryFn: async () => {
      const res = await MetaLambdaFetch('/getCreativeFatigueSummary', { params: { company } });
      return awaitJSON<CreativeFatigueSummaryRow[]>(res);
    },
    enabled: Boolean(company)
  });

export const useCreativeFatiguedAds = (company: string | null) =>
  useQuery({
    queryKey: ['metaFatigueCreativeFatiguedAds', company],
    queryFn: async () => {
      const res = await MetaLambdaFetch('/getCreativeFatiguedAds', { params: { company } });
      return awaitJSON<CreativeFatiguedAdsRow[]>(res);
    },
    enabled: Boolean(company)
  });

export const useAdsetStatus = (company: string | null) =>
  useQuery({
    queryKey: ['metaFatigueAdsetStatus', company],
    queryFn: async () => {
      const res = await MetaLambdaFetch('/getAdsetStatus', { params: { company } });
      return awaitJSON<AdsetStatusRow[]>(res);
    },
    enabled: Boolean(company)
  });

// ── Date-range-scoped ────────────────────────────────────────────────────────
// placeholderData: keepPreviousData keeps the last result (and `data` truthy) while a
// date-range/KPI change is refetching. otherwise the whole page body unmounts to a bare spinner
// instead of the per-panel `isRefetching`

export const useAdSetPerformanceTotals = (company: string | null, dateRange: DateRange) =>
  useQuery({
    queryKey: ['metaFatigueAdSetPerformanceTotals', company, dateRange.start, dateRange.end],
    queryFn: async () => {
      const res = await MetaLambdaFetch('/getAdSetPerformanceTotals', {
        params: { company, start: dateRange.start, end: dateRange.end }
      });
      return awaitJSON<AdSetPerformanceTotalsRow[]>(res);
    },
    enabled: Boolean(company),
    placeholderData: keepPreviousData
  });

export const useAdPerformanceTotals = (company: string | null, dateRange: DateRange) =>
  useQuery({
    queryKey: ['metaFatigueAdPerformanceTotals', company, dateRange.start, dateRange.end],
    queryFn: async () => {
      const res = await MetaLambdaFetch('/getAdPerformanceTotals', {
        params: { company, start: dateRange.start, end: dateRange.end }
      });
      return awaitJSON<AdPerformanceTotalsRow[]>(res);
    },
    enabled: Boolean(company),
    placeholderData: keepPreviousData
  });

export const useAdSetDailyPerformance = (company: string | null, dateRange: DateRange) =>
  useQuery({
    queryKey: ['metaFatigueAdSetDailyPerformance', company, dateRange.start, dateRange.end],
    queryFn: async () => {
      const res = await MetaLambdaFetch('/getAdSetDailyPerformance', {
        params: { company, start: dateRange.start, end: dateRange.end }
      });
      return awaitJSON<AdSetDailyPerformanceRow[]>(res);
    },
    enabled: Boolean(company),
    placeholderData: keepPreviousData
  });

export const useAdsetPlacementBreakdown = (company: string | null, dateRange: DateRange) =>
  useQuery({
    queryKey: ['metaFatigueAdsetPlacementBreakdown', company, dateRange.start, dateRange.end],
    queryFn: async () => {
      const res = await MetaLambdaFetch('/getAdsetPlacementBreakdown', {
        params: { company, start: dateRange.start, end: dateRange.end }
      });
      return awaitJSON<AdsetPlacementBreakdownRow[]>(res);
    },
    enabled: Boolean(company),
    placeholderData: keepPreviousData
  });

export const useAdKpiVolumeRevenue = (company: string | null, dateRange: DateRange, kpi: string) =>
  useQuery({
    queryKey: ['metaFatigueAdKpiVolumeRevenue', company, dateRange.start, dateRange.end, kpi],
    queryFn: async () => {
      const res = await MetaLambdaFetch('/getAdKpiVolumeRevenue', {
        params: { company, start: dateRange.start, end: dateRange.end, kpi }
      });
      return awaitJSON<AdKpiVolumeRevenueRow[]>(res);
    },
    enabled: Boolean(company) && Boolean(kpi),
    placeholderData: keepPreviousData
  });
